/**
 * Frontier program F-3 — bias-corrected MSE estimation (frozen plan:
 * validation/simulation/FRONTIER_F3_PLAN.md, 2026-10-24). Study-only
 * machinery behind UncertaintyConfig.studyMseCandidate; no production
 * caller sets the switch, and with the switch absent every result of
 * this module is unreachable — the shipped default path is untouched.
 *
 * Candidates (plan §3, frozen):
 *   A — Hall & Maiti (2006) parametric double bootstrap, bc1 form,
 *       B1 = 100 first-level, B2 = 25 second-level replicates.
 *   B — second-order analytic MSE: Datta & Lahiri (2003) completion
 *       of the shipped Prasad–Rao/Datta–Lahiri form plus the
 *       Slud & Maiti (2006) propagation to the proportion scale.
 *   C — hybrid: Hall–Maiti bias correction with the shipped analytic
 *       estimator as the object corrected; one bootstrap layer,
 *       B = 200 (the shipped B).
 *
 * Bootstrap candidates run the full published pipeline inside every
 * replicate (fit with the shipped boundary cascade + bc
 * back-transformation + faithful-target benchmarking, selection
 * fixed at the observed-data set), exactly as bootstrap.ts does.
 * The replicate refits use the lean refit below: it replicates the
 * shipped cascade's formulas and tolerances (fayherriot.ts
 * fitVariance / weightedQuantities, Prasad–Rao start, REML Fisher
 * scoring, Li–Lahiri adjusted REML on the boundary, Prasad–Rao on
 * non-convergence) on a precomputed standardized design, skipping
 * only the fit diagnostics and the R^2 refit, which no replicate
 * consumer reads. Draw consumption matches bootstrap.ts exactly —
 * per area in supplied domain order, first v*, then e* when the
 * area is in sample, then the faithful target draw — from
 * createReplicateStream(seed, b); second-level streams derive from
 * (seed, b, c) as createReplicateStream(`${seed}::f3-second::${b}`,
 * c), so every stream is a pure function of its indices and results
 * are chunking-invariant (VAL-03 form). Benchmarking's logit shift
 * is solved by safeguarded Newton to the same equation
 * benchmarkPointEstimates solves by bisection (agreement to the
 * solver tolerance, 1e-12 on the weighted mean).
 */
import { backTransformAreasImpl } from './backtransform.js';
import { DEFAULT_FAY_HERRIOT_FIT_CONFIG } from './config.js';
import { normalQuantile } from './distributions.js';
import { fitFayHerriotImpl } from './fayherriot.js';
import { cholesky, cholInverse, dot, matMul, matVec, traceOfProduct, zeros, } from './linalg.js';
import { clamp, inverseLogit, logit } from './numeric.js';
import { createReplicateStream } from './prng.js';
import { arcsineExactExpectationCorrection, arcsineMeanBiasCorrection, } from './transform.js';
const HALF_PI = Math.PI / 2;
const clampTheta = (t) => Math.min(Math.max(t, 0), HALF_PI);
/** Frozen replicate counts (plan §3.1 / §3.3). */
export const F3_A_B1 = 100;
export const F3_A_B2 = 25;
export const F3_C_B = 200;
/**
 * Hall–Maiti bc1 combination (plan §3.1 step 5), per scale:
 * 2u - v when positive, else the positive exponential fallback,
 * else u when v is not usable.
 */
export function bc1Combine(u, v) {
    const add = 2 * u - v;
    if (add > 0)
        return add;
    if (v > 0)
        return u * Math.exp(-(v - u) / v);
    return u;
}
function meanOf(v) {
    return v.reduce((a, b) => a + b, 0) / v.length;
}
function medianOf(v) {
    const s = [...v].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 === 1 ? s[mid] : 0.5 * (s[mid - 1] + s[mid]);
}
function sampleVariance(v) {
    if (v.length < 2)
        return 0;
    const mean = meanOf(v);
    return v.reduce((a, x) => a + (x - mean) * (x - mean), 0) / (v.length - 1);
}
function createLeanContext(domains, benchmarking, populationCounts, fitConfig) {
    const inSample = domains.filter((d) => !d.outOfSample);
    const m = inSample.length;
    const p = inSample[0].covariates.length;
    const centers = new Array(p).fill(0);
    const scales = new Array(p).fill(1);
    if (fitConfig.standardizeCovariates) {
        for (let j = 1; j < p; j++) {
            const column = inSample.map((d) => d.covariates[j]);
            const sd = Math.sqrt(sampleVariance(column));
            if (sd > 0 && Number.isFinite(sd)) {
                centers[j] = meanOf(column);
                scales[j] = sd;
            }
        }
    }
    const xsAll = domains.map((d) => d.covariates.map((v, j) => (v - centers[j]) / scales[j]));
    const inIdx = [];
    domains.forEach((d, i) => {
        if (!d.outOfSample)
            inIdx.push(i);
    });
    const psi = inSample.map((d) => d.psi);
    // Prasad–Rao start caches: (X'X)^-1 and leverages on the
    // standardized design (constant across replicate worlds).
    const XtX = zeros(p, p);
    for (const i of inIdx) {
        const xi = xsAll[i];
        for (let a = 0; a < p; a++) {
            for (let b = 0; b < p; b++)
                XtX[a][b] += xi[a] * xi[b];
        }
    }
    const L = cholesky(XtX);
    if (L === null) {
        throw new Error('frontierf3: singular cross-product (X\'X) in cell context.');
    }
    const invXtX = cholInverse(L);
    const leverage = inIdx.map((i) => dot(xsAll[i], matVec(invXtX, xsAll[i])));
    let benchmarkOmegas = null;
    if (benchmarking.external !== 'none') {
        if (populationCounts === undefined) {
            throw new Error('frontierf3: external benchmarking is enabled but no population counts were supplied.');
        }
        const raw = domains.map((d) => {
            const n = populationCounts[d.areaId];
            if (n === undefined || !Number.isFinite(n) || n <= 0) {
                throw new Error(`frontierf3: no usable population count for area "${d.areaId}".`);
            }
            return n;
        });
        const total = raw.reduce((a, b) => a + b, 0);
        benchmarkOmegas = raw.map((n) => n / total);
    }
    return {
        domains,
        m,
        p,
        xsAll,
        inIdx,
        psi,
        medianPsi: medianOf(psi),
        invXtX,
        leverage,
        benchmarkOmegas,
    };
}
function weightedQuantities(ctx, y, sigma2) {
    const { m, p } = ctx;
    const M = zeros(p, p);
    const M2 = zeros(p, p);
    const M3 = zeros(p, p);
    const rhs = new Array(p).fill(0);
    let sumQ = 0;
    let sumQ2 = 0;
    for (let k = 0; k < m; k++) {
        const xi = ctx.xsAll[ctx.inIdx[k]];
        const q = 1 / (sigma2 + ctx.psi[k]);
        const q2 = q * q;
        const q3 = q2 * q;
        sumQ += q;
        sumQ2 += q2;
        for (let a = 0; a < p; a++) {
            rhs[a] += q * xi[a] * y[k];
            for (let b = 0; b < p; b++) {
                const xab = xi[a] * xi[b];
                M[a][b] += q * xab;
                M2[a][b] += q2 * xab;
                M3[a][b] += q3 * xab;
            }
        }
    }
    const L = cholesky(M);
    if (L === null)
        throw new Error('frontierf3: singular weighted design (M).');
    const invM = cholInverse(L);
    const beta = matVec(invM, rhs);
    const residuals = y.map((yi, k) => yi - dot(ctx.xsAll[ctx.inIdx[k]], beta));
    const K = matMul(invM, M2);
    let trK = 0;
    for (let a = 0; a < p; a++)
        trK += K[a][a];
    const trP = sumQ - trK;
    const trP2 = sumQ2 - 2 * traceOfProduct(invM, M3) + traceOfProduct(K, K);
    let sumQ2r2 = 0;
    for (let k = 0; k < m; k++) {
        const q = 1 / (sigma2 + ctx.psi[k]);
        sumQ2r2 += q * q * residuals[k] * residuals[k];
    }
    return {
        beta,
        invM,
        residuals,
        score: -0.5 * trP + 0.5 * sumQ2r2,
        info: 0.5 * trP2,
        trP,
        trP2,
    };
}
function stepTolerance(tol, sigma2, medianPsi) {
    return tol * (sigma2 + medianPsi);
}
function leanFitFromSigma(ctx, y, sigma2, methodUsed, wq) {
    const { m } = ctx;
    const s = sigma2;
    let vbar;
    if (methodUsed === 'prasad-rao') {
        let sumSq = 0;
        for (let k = 0; k < m; k++)
            sumSq += (s + ctx.psi[k]) * (s + ctx.psi[k]);
        vbar = (2 / (m * m)) * sumSq;
    }
    else {
        let sumNeg2 = 0;
        for (let k = 0; k < m; k++)
            sumNeg2 += Math.pow(s + ctx.psi[k], -2);
        vbar = 2 / sumNeg2;
    }
    const inPos = new Map(ctx.inIdx.map((di, k) => [di, k]));
    const areas = ctx.domains.map((d, di) => {
        const xs = ctx.xsAll[di];
        const syntheticTheta = dot(xs, wq.beta);
        const xInvMx = dot(xs, matVec(wq.invM, xs));
        if (d.outOfSample) {
            return {
                gamma: 0, g1: 0, g2: 0, g3: 0,
                mseTheta: s + xInvMx, eblupTheta: syntheticTheta, syntheticTheta, xInvMx,
            };
        }
        const k = inPos.get(di);
        const gamma = s / (s + d.psi);
        const g1 = gamma * d.psi;
        const g2 = (1 - gamma) * (1 - gamma) * xInvMx;
        const g3 = d.psi * d.psi * Math.pow(s + d.psi, -3) * vbar;
        return {
            gamma, g1, g2, g3,
            mseTheta: g1 + g2 + 2 * g3,
            eblupTheta: gamma * y[k] + (1 - gamma) * syntheticTheta,
            syntheticTheta, xInvMx,
        };
    });
    return { sigma2: s, methodUsed, beta: wq.beta, areas };
}
function prasadRaoSigma2(ctx, y) {
    const { m, p } = ctx;
    const betaOls = (() => {
        const Xty = new Array(p).fill(0);
        for (let k = 0; k < m; k++) {
            const xi = ctx.xsAll[ctx.inIdx[k]];
            for (let a = 0; a < p; a++)
                Xty[a] += xi[a] * y[k];
        }
        return matVec(ctx.invXtX, Xty);
    })();
    let sumU2 = 0;
    let sumPsiH = 0;
    for (let k = 0; k < m; k++) {
        const u = y[k] - dot(ctx.xsAll[ctx.inIdx[k]], betaOls);
        sumU2 += u * u;
        sumPsiH += ctx.psi[k] * (1 - ctx.leverage[k]);
    }
    return Math.max(0, (sumU2 - sumPsiH) / (m - p));
}
function fitAdjustedRemlLean(ctx, y, config, start) {
    const tol = config.convergenceTolerance;
    const medianPsi = ctx.medianPsi;
    const yVar = sampleVariance(y);
    const upper = 10 * yVar + Math.max(...ctx.psi);
    let s = start > 0 ? Math.min(start, upper) : Math.min(0.1 * medianOf(ctx.psi), upper);
    if (!(s > 0))
        s = Math.min(1e-6, upper);
    for (let it = 1; it <= config.maxIterations; it++) {
        const w = weightedQuantities(ctx, y, s);
        const aScore = w.score + 1 / s;
        const aInfo = w.info + 1 / (s * s);
        if (!Number.isFinite(aScore) || !Number.isFinite(aInfo) || aInfo <= 0)
            break;
        let step = aScore / aInfo;
        let sNew = s + step;
        let halvings = 0;
        while ((!(sNew > 0) || sNew > upper || !Number.isFinite(sNew)) && halvings < 100) {
            step /= 2;
            sNew = s + step;
            halvings++;
        }
        if (!(sNew > 0) || sNew > upper || !Number.isFinite(sNew))
            break;
        if (Math.abs(sNew - s) < stepTolerance(tol, sNew, medianPsi)) {
            s = sNew;
            break;
        }
        s = sNew;
    }
    return { sigma2: s };
}
/** The shipped brief 9.7 cascade, lean form (fayherriot.ts fitVariance). */
function leanFit(ctx, y, config) {
    const tol = config.convergenceTolerance;
    const medianPsi = ctx.medianPsi;
    const sigma2Pr = prasadRaoSigma2(ctx, y);
    const start = Math.max(sigma2Pr, 0.1 * medianPsi);
    if (config.studyAdjustedRemlForAll === true) {
        const adj = fitAdjustedRemlLean(ctx, y, config, start);
        return leanFitFromSigma(ctx, y, adj.sigma2, 'adjusted-reml', weightedQuantities(ctx, y, adj.sigma2));
    }
    let s = start;
    let converged = false;
    let boundary = start <= 0;
    if (!boundary) {
        for (let it = 1; it <= config.maxIterations; it++) {
            const w = weightedQuantities(ctx, y, s);
            if (!Number.isFinite(w.score) || !Number.isFinite(w.info) || w.info <= 0)
                break;
            const sNew = s + w.score / w.info;
            if (sNew <= 0) {
                boundary = true;
                break;
            }
            if (Math.abs(sNew - s) < stepTolerance(tol, sNew, medianPsi)) {
                s = sNew;
                converged = true;
                break;
            }
            s = sNew;
        }
    }
    if (boundary) {
        const adj = fitAdjustedRemlLean(ctx, y, config, start);
        return leanFitFromSigma(ctx, y, adj.sigma2, 'adjusted-reml', weightedQuantities(ctx, y, adj.sigma2));
    }
    if (!converged) {
        if (sigma2Pr > 0) {
            return leanFitFromSigma(ctx, y, sigma2Pr, 'prasad-rao', weightedQuantities(ctx, y, sigma2Pr));
        }
        const adj = fitAdjustedRemlLean(ctx, y, config, start);
        return leanFitFromSigma(ctx, y, adj.sigma2, 'adjusted-reml', weightedQuantities(ctx, y, adj.sigma2));
    }
    return leanFitFromSigma(ctx, y, s, 'reml', weightedQuantities(ctx, y, s));
}
function runWorld(ctx, syntheticTheta, sigma2, seed, replicate, config, options) {
    const { domains } = ctx;
    const mAll = domains.length;
    const linear = options.transform === 'linear';
    const stream = createReplicateStream(seed, replicate);
    const sdV = Math.sqrt(sigma2);
    const thetaStar = new Array(mAll);
    const pStar = new Array(mAll);
    const thetaHatRaw = new Array(mAll);
    const yIn = [];
    const directProportion = (t) => linear ? Math.min(1, Math.max(0, t)) : Math.sin(clampTheta(t)) ** 2;
    for (let i = 0; i < mAll; i++) {
        const d = domains[i];
        const vStar = sdV * stream.nextNormal();
        const tStar = syntheticTheta[i] + vStar;
        thetaStar[i] = tStar;
        pStar[i] = linear ? tStar : Math.sin(clampTheta(tStar)) ** 2;
        if (d.outOfSample) {
            thetaHatRaw[i] = d.thetaHat;
        }
        else {
            const eStar = Math.sqrt(d.psi) * stream.nextNormal();
            let thetaHatStar = tStar + eStar;
            thetaHatRaw[i] = thetaHatStar;
            if (options.meanBiasCorrection === true && !linear && d.psi > 0) {
                thetaHatStar +=
                    options.meanBiasCorrectionForm === 'exact-expectation'
                        ? arcsineExactExpectationCorrection(directProportion(thetaHatStar), 1 / (4 * d.psi))
                        : arcsineMeanBiasCorrection(directProportion(thetaHatStar), 1 / (4 * d.psi));
            }
            yIn.push(thetaHatStar);
        }
    }
    const fitB = leanFit(ctx, yIn, config);
    // bc back-transformation (unbenchmarked), as bootstrap.ts.
    const ptilde = new Array(mAll);
    const mseDLStarP = new Array(mAll);
    for (let i = 0; i < mAll; i++) {
        const a = fitB.areas[i];
        if (linear) {
            ptilde[i] = Math.min(1, Math.max(0, a.eblupTheta));
            mseDLStarP[i] = a.mseTheta;
        }
        else {
            const theta = clampTheta(a.eblupTheta);
            const s2 = a.g1 + a.g2;
            ptilde[i] = 0.5 * (1 - Math.cos(2 * theta) * Math.exp(-2 * s2));
            // Interval-derived square-equivalent at the run's confidence:
            // rmse from the same map backTransformAreasImpl applies.
            const bt = backTransformOne(a.eblupTheta, a.mseTheta, options.confidence);
            mseDLStarP[i] = bt * bt;
        }
    }
    let thetaEstimates = fitB.areas.map((a) => a.eblupTheta);
    let pEstimates = ptilde;
    if (ctx.benchmarkOmegas !== null) {
        const omegas = ctx.benchmarkOmegas;
        let weightSum = 0;
        let weightedTruth = 0;
        let varSum = 0;
        for (let i = 0; i < mAll; i++) {
            if (domains[i].outOfSample)
                continue;
            weightSum += omegas[i];
            weightedTruth += omegas[i] * pStar[i];
            const deriv = linear ? 1 : Math.sin(2 * clampTheta(thetaStar[i]));
            varSum += omegas[i] * omegas[i] * deriv * deriv * domains[i].psi;
        }
        const truthAggregate = weightedTruth / weightSum;
        const varTarget = varSum / (weightSum * weightSum);
        const replicateTarget = truthAggregate + Math.sqrt(varTarget) * stream.nextNormal();
        const bench = benchmarkPointsFast(ptilde, omegas, options.benchmarking.external, replicateTarget);
        pEstimates = bench;
        thetaEstimates = linear
            ? bench
            : bench.map((p) => Math.asin(Math.sqrt(Math.min(1, Math.max(0, p)))));
    }
    const qTheta = new Array(mAll);
    const qP = new Array(mAll);
    for (let i = 0; i < mAll; i++) {
        const dT = thetaEstimates[i] - thetaStar[i];
        qTheta[i] = dT * dT;
        const dP = pEstimates[i] - pStar[i];
        qP[i] = dP * dP;
    }
    return {
        fit: fitB,
        qTheta,
        qP,
        mseDLStarTheta: fitB.areas.map((a) => a.mseTheta),
        mseDLStarP,
    };
}
/** Interval-derived RMSE of one area (backtransform.ts arcsine map). */
function backTransformOne(eblupTheta, mseTheta, confidence) {
    const z = normalQuantile(0.5 + confidence / 2);
    const half = z * Math.sqrt(Math.max(0, mseTheta));
    const theta = clampTheta(eblupTheta);
    const lo = Math.sin(clampTheta(theta - half)) ** 2;
    const hi = Math.sin(clampTheta(theta + half)) ** 2;
    return (hi - lo) / (2 * z);
}
/** Logit/ratio benchmarking of point estimates (benchmarking.ts
 *  equation; logit shift by safeguarded Newton, bisection fallback). */
export function benchmarkPointsFast(values, omegas, method, target) {
    if (method === 'ratio') {
        let wm = 0;
        for (let i = 0; i < values.length; i++)
            wm += omegas[i] * values[i];
        if (!(wm > 0))
            throw new Error('frontierf3: ratio benchmarking impossible (weighted mean 0).');
        const factor = target / wm;
        const out = values.map((v) => v * factor);
        for (const v of out) {
            if (v > 1)
                throw new Error('frontierf3: ratio benchmarking rejected (result above 1).');
        }
        return out;
    }
    const logits = values.map((v) => logit(clamp(v, 1e-6, 1 - 1e-6)));
    const meanAt = (delta) => {
        let s = 0;
        for (let i = 0; i < values.length; i++)
            s += omegas[i] * inverseLogit(logits[i] + delta);
        return s;
    };
    let delta = 0;
    let solved = false;
    for (let it = 0; it < 60; it++) {
        let f = -target;
        let fp = 0;
        for (let i = 0; i < values.length; i++) {
            const p = inverseLogit(logits[i] + delta);
            f += omegas[i] * p;
            fp += omegas[i] * p * (1 - p);
        }
        if (Math.abs(f) < 1e-13) {
            solved = true;
            break;
        }
        if (!(fp > 0))
            break;
        let step = f / fp;
        if (step > 5)
            step = 5;
        if (step < -5)
            step = -5;
        delta -= step;
        if (!Number.isFinite(delta))
            break;
    }
    if (!solved) {
        let lo = -50;
        let hi = 50;
        for (let it = 0; it < 200; it++) {
            const mid = 0.5 * (lo + hi);
            if (meanAt(mid) < target)
                lo = mid;
            else
                hi = mid;
            if (hi - lo < 1e-14)
                break;
        }
        delta = 0.5 * (lo + hi);
    }
    return values.map((_, i) => inverseLogit(logits[i] + delta));
}
/** dg1/ds at fixed psi (plan §3.2): psi^2 / (s + psi)^2. */
export function f3Dg1Ds(s, psi) {
    return (psi * psi) / ((s + psi) * (s + psi));
}
/**
 * dg2/ds holding xInvMx at its fitted value (plan §3.2 form):
 * -2 (1 - gamma)^2 xInvMx / (s + psi), from
 * d(1 - gamma)/ds = -(1 - gamma) / (s + psi).
 */
export function f3Dg2Ds(s, psi, xInvMx) {
    const gamma = s / (s + psi);
    const omg = 1 - gamma;
    return (-2 * omg * omg * xInvMx) / (s + psi);
}
export function f3CandidateB(fit, domains, options = {}) {
    const fitConfig = options.fitConfig ?? DEFAULT_FAY_HERRIOT_FIT_CONFIG;
    const linear = options.transform === 'linear';
    const s = fit.sigma2v;
    // Standardized design + WQ at the fitted s, for xInvMx and the
    // Datta–Lahiri bias term's weighted-leverage form.
    const ctx = createLeanContext(domains, { external: 'none', internal: false }, undefined, fitConfig);
    const yIn = ctx.inIdx.map((i) => domains[i].thetaHat);
    const wq = weightedQuantities(ctx, yIn, s);
    // b(s): Datta–Lahiri form for the shipped cascade (plan §3.2).
    // REML / Prasad–Rao: the O(1/m) bias is the psi^-2-weighted mean
    // leverage, b(s) = - sum_j q_j^2 xInvMx_j / sum_j q_j^2 with
    // q_j = 1/(s + psi_j) — O(1/m), vanishing as m grows, formed from
    // the engine's own weighted quantities (no new sums beyond the
    // fit's). Adjusted-REML: b = 0 by the Li–Lahiri construction (the
    // adjustment annihilates this term; plan §3.2).
    let bSigma2 = 0;
    if (fit.methodUsed !== 'adjusted-reml') {
        let num = 0;
        let den = 0;
        ctx.inIdx.forEach((di, k) => {
            const q = 1 / (s + ctx.psi[k]);
            const xs = ctx.xsAll[di];
            const xInvMx = dot(xs, matVec(wq.invM, xs));
            num += q * q * xInvMx;
            den += q * q;
        });
        bSigma2 = den > 0 ? -num / den : 0;
    }
    let fallbackCount = 0;
    const areas = fit.areas.map((a, idx) => {
        if (a.outOfSample) {
            // Shipped out-of-sample form retained (plan §3.2): no g3/bias
            // term exists for a synthetic estimator at this order. mseP by
            // the same Slud–Maiti propagation with (g1 + g2) = 0.
            const mseTheta = a.mseTheta;
            if (linear)
                return { areaId: a.areaId, mseTheta, mseP: mseTheta };
            const theta = clampTheta(a.eblupTheta);
            const hp = Math.sin(2 * theta);
            const hpp = 2 * Math.cos(2 * theta);
            const biasP = 0.5 * hpp * mseTheta;
            return {
                areaId: a.areaId,
                mseTheta,
                mseP: hp * hp * mseTheta + biasP * biasP,
            };
        }
        const psi = a.psi;
        const xs = ctx.xsAll[idx];
        const xInvMx = dot(xs, matVec(wq.invM, xs));
        const dg1 = f3Dg1Ds(s, psi);
        const dg2 = f3Dg2Ds(s, psi, xInvMx);
        let mseTheta = a.g1 + a.g2 + 2 * a.g3 - bSigma2 * (dg1 + dg2);
        if (!(mseTheta > 0)) {
            mseTheta = a.g1 + a.g2 + 2 * a.g3;
            fallbackCount += 1;
        }
        if (linear)
            return { areaId: a.areaId, mseTheta, mseP: mseTheta };
        const theta = clampTheta(a.eblupTheta);
        const hp = Math.sin(2 * theta);
        const hpp = 2 * Math.cos(2 * theta);
        const s2 = a.g1 + a.g2;
        const biasP = 0.5 * hpp * (mseTheta - s2);
        const mseP = hp * hp * mseTheta + 0.5 * hpp * hpp * s2 * s2 + biasP * biasP;
        return { areaId: a.areaId, mseTheta, mseP };
    });
    return { areas, fallbackCount, bSigma2 };
}
function observedMseDL(fit, confidence, linear) {
    const bt = backTransformAreasImpl(fit, {
        method: 'bc',
        confidence,
        transform: linear ? 'linear' : 'arcsin',
    });
    const rmseByArea = new Map(bt.map((e) => [e.areaId, e.rmse]));
    return fit.areas.map((a) => ({
        areaId: a.areaId,
        mseTheta: a.mseTheta,
        mseP: (rmseByArea.get(a.areaId) ?? 0) ** 2,
    }));
}
export function f3CandidateC(fit, domains, options) {
    const fitConfig = options.fitConfig ?? DEFAULT_FAY_HERRIOT_FIT_CONFIG;
    const B = options.replicates ?? F3_C_B;
    const linear = options.transform === 'linear';
    const ctx = createLeanContext(domains, options.benchmarking, options.populationCounts, fitConfig);
    const syntheticTheta = fit.areas.map((a) => a.syntheticTheta);
    const mAll = domains.length;
    const sumQTheta = new Float64Array(mAll);
    const sumQP = new Float64Array(mAll);
    const sumDLTheta = new Float64Array(mAll);
    const sumDLP = new Float64Array(mAll);
    for (let b = 0; b < B; b++) {
        const w = runWorld(ctx, syntheticTheta, fit.sigma2v, options.seed, b, fitConfig, options);
        for (let i = 0; i < mAll; i++) {
            sumQTheta[i] += w.qTheta[i];
            sumQP[i] += w.qP[i];
            sumDLTheta[i] += w.mseDLStarTheta[i];
            sumDLP[i] += w.mseDLStarP[i];
        }
    }
    const mseDL = observedMseDL(fit, options.confidence, linear);
    const areas = [];
    const uHat = [];
    const vHat = [];
    for (let i = 0; i < mAll; i++) {
        const areaId = domains[i].areaId;
        const uT = sumQTheta[i] / B;
        const uP = sumQP[i] / B;
        const vT = sumDLTheta[i] / B;
        const vP = sumDLP[i] / B;
        uHat.push({ areaId, mseTheta: uT, mseP: uP });
        vHat.push({ areaId, mseTheta: vT, mseP: vP });
        // Corrected estimator with bc1 positivity (plan §3.3 step 4):
        // mseC = mseDL - v + u when positive; else the exponential
        // fallback on the same estimate-minus-expectation form.
        const corrT = mseDL[i].mseTheta - vT + uT;
        const corrP = mseDL[i].mseP - vP + uP;
        areas.push({
            areaId,
            mseTheta: corrT > 0
                ? corrT
                : vT > 0
                    ? uT * Math.exp(-(vT - mseDL[i].mseTheta) / vT)
                    : uT,
            mseP: corrP > 0
                ? corrP
                : vP > 0
                    ? uP * Math.exp(-(vP - mseDL[i].mseP) / vP)
                    : uP,
        });
    }
    return { areas, uHat, vHat, mseDL, replicates: B };
}
export function f3CandidateA(fit, domains, options) {
    const fitConfig = options.fitConfig ?? DEFAULT_FAY_HERRIOT_FIT_CONFIG;
    const B1 = options.b1 ?? F3_A_B1;
    const B2 = options.b2 ?? F3_A_B2;
    const ctx = createLeanContext(domains, options.benchmarking, options.populationCounts, fitConfig);
    const syntheticTheta = fit.areas.map((a) => a.syntheticTheta);
    const mAll = domains.length;
    const sumQTheta = new Float64Array(mAll);
    const sumQP = new Float64Array(mAll);
    const sumVTheta = new Float64Array(mAll);
    const sumVP = new Float64Array(mAll);
    for (let b = 0; b < B1; b++) {
        const w = runWorld(ctx, syntheticTheta, fit.sigma2v, options.seed, b, fitConfig, options);
        for (let i = 0; i < mAll; i++) {
            sumQTheta[i] += w.qTheta[i];
            sumQP[i] += w.qP[i];
        }
        // Second level: worlds generated from refit b's parameters.
        const synthB = w.fit.areas.map((a) => a.syntheticTheta);
        const seedB = `${options.seed}::f3-second::${b}`;
        const sumStarTheta = new Float64Array(mAll);
        const sumStarP = new Float64Array(mAll);
        for (let c = 0; c < B2; c++) {
            const w2 = runWorld(ctx, synthB, w.fit.sigma2, seedB, c, fitConfig, options);
            for (let i = 0; i < mAll; i++) {
                sumStarTheta[i] += w2.qTheta[i];
                sumStarP[i] += w2.qP[i];
            }
        }
        for (let i = 0; i < mAll; i++) {
            sumVTheta[i] += sumStarTheta[i] / B2;
            sumVP[i] += sumStarP[i] / B2;
        }
    }
    const areas = [];
    const uHat = [];
    const vHat = [];
    for (let i = 0; i < mAll; i++) {
        const areaId = domains[i].areaId;
        const uT = sumQTheta[i] / B1;
        const uP = sumQP[i] / B1;
        const vT = sumVTheta[i] / B1;
        const vP = sumVP[i] / B1;
        uHat.push({ areaId, mseTheta: uT, mseP: uP });
        vHat.push({ areaId, mseTheta: vT, mseP: vP });
        areas.push({ areaId, mseTheta: bc1Combine(uT, vT), mseP: bc1Combine(uP, vP) });
    }
    return { areas, uHat, vHat, b1: B1, b2: B2 };
}
/**
 * Dispatch used by the analysis layer's study switch: fit the cell
 * by the shipped fit when the caller has not already done so is NOT
 * done here — the caller supplies the observed fit so point
 * estimates stay bit-identical between arms (plan §5.5(iii-a)).
 */
export function f3Candidate(candidate, fit, domains, options) {
    if (candidate === 'B') {
        const r = f3CandidateB(fit, domains, {
            transform: options.transform,
            fitConfig: options.fitConfig,
        });
        return { areas: r.areas, fallbackCount: r.fallbackCount };
    }
    if (candidate === 'A') {
        return { areas: f3CandidateA(fit, domains, options).areas, fallbackCount: 0 };
    }
    return { areas: f3CandidateC(fit, domains, options).areas, fallbackCount: 0 };
}
/** Fit-then-candidate convenience for tests and audit fixtures. */
export function f3CandidateFromDomains(candidate, domains, options) {
    const fit = fitFayHerriotImpl(domains, options.fitConfig ?? DEFAULT_FAY_HERRIOT_FIT_CONFIG);
    return { ...f3Candidate(candidate, fit, domains, options), fit };
}
//# sourceMappingURL=frontierf3.js.map